import { ERROR_CODES, isApiError, type ErrorCode } from '@tg/shared';

/**
 * Typed HTTP client for /api/*.
 * - cookies: credentials 'include' (httpOnly session cookie)
 * - every mutating request sends `x-requested-with: tg` (CSRF guard on the server)
 * - non-2xx bodies `{ error, params? }` become an ApiRequestError with that code
 * - network failure / non-JSON 5xx → code 'NETWORK' (client-only, translated via common.*)
 */
export type ClientErrorCode = ErrorCode | 'NETWORK';

export class ApiRequestError extends Error {
  readonly code: ClientErrorCode;
  readonly params: Record<string, string | number>;
  readonly status: number;
  constructor(code: ClientErrorCode, status: number, params: Record<string, string | number> = {}) {
    super(code);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.params = params;
  }
  get isNetwork(): boolean {
    return this.code === 'NETWORK';
  }
}

export function isApiRequestError(e: unknown): e is ApiRequestError {
  return e instanceof ApiRequestError;
}

export function isKnownErrorCode(x: string): x is ErrorCode {
  return (ERROR_CODES as readonly string[]).includes(x);
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  signal?: AbortSignal;
  cache?: RequestCache;
  headers?: Record<string, string>;
}

const BASE = '';

async function request<T>(method: Method, path: string, body?: unknown, o: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', ...o.headers };
  if (method !== 'GET') headers['x-requested-with'] = 'tg';
  if (body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: o.signal,
      cache: o.cache,
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new ApiRequestError('NETWORK', 0);
  }

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }

  if (!res.ok) {
    if (isApiError(data)) {
      const code = isKnownErrorCode(data.error) ? data.error : 'INTERNAL';
      throw new ApiRequestError(code, res.status, data.params ?? {});
    }
    if (res.status === 401) throw new ApiRequestError('UNAUTHORIZED', 401);
    // Vite proxy answers 502/504 with HTML when the server is down.
    if (res.status >= 500 || data === undefined) throw new ApiRequestError('NETWORK', res.status);
    throw new ApiRequestError('INTERNAL', res.status);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, o?: RequestOptions) => request<T>('GET', path, undefined, o),
  post: <T>(path: string, body?: unknown, o?: RequestOptions) => request<T>('POST', path, body ?? {}, o),
  patch: <T>(path: string, body?: unknown, o?: RequestOptions) => request<T>('PATCH', path, body ?? {}, o),
  put: <T>(path: string, body?: unknown, o?: RequestOptions) => request<T>('PUT', path, body ?? {}, o),
  del: <T>(path: string, o?: RequestOptions) => request<T>('DELETE', path, undefined, o),
};

/** Idempotency key for every bet-like request (zIdempotencyKey: 8..64 chars). */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
